import React, { useContext, useState } from "react";
import cx from "classnames";
import BillRecap from "Components/BillRecap/BillRecap";
import Button from "Components/button/Button";
import ServicesRecapTable from "Components/servicesRecapTable/ServicesRecapTable";
import TranslatedString from "Components/TranslatedString";
import { t } from "i18next";
import { getAccountTypeTranslation } from "Utils/commonTranslations";
import SmallModal from "Components/smallModal/smallModal";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { AuthContext } from "@/App";
import { deliveryPlans } from "@/config";
import { alertError, alertSuccess } from "Utils/alerts";
import WarningBanner from "Components/warningBanner/WarningBanner";

const formatter = new Intl.NumberFormat(navigator.language || navigator.userLanguage, {
  maximumFractionDigits: 0,
});

const BillingInfoRow = ({ label, value }) => {
  return (
    <div className="flex border-solid border-0 border-b border-[--bg-d5] py-2">
      <div className="flex-1 text-[--text-a40] align-middle">{label}:</div>
      <div className="flex-[2] font-semibold text-center flex items-center">{value || "-"}</div>
    </div>
  );
};

const Recap = ({
  stepper,
  hidePrices,
  formData,
  onFormCancel,
  api,
  isFormLoading,
  nextStep,
  unsetFormLoading,
  setFormLoading,
  setNewUserData,
  handleFormResponseErrors,
  resetForm,
  onFormSubmit = () => {},
  hasBillingInfo,
  isUpdating,
  selectedTier,
  isLoggedUserAdmin,
  formErrors,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const history = useHistory();
  const { getLoggedUser } = useContext(AuthContext);
  const isReseller = getLoggedUser().is_reseller;

  let appliedDiscount = null;
  if (isReseller) {
    const discount = selectedTier.special_tiers.find((t) => t.name === "Reseller");
    if (discount) appliedDiscount = discount;
  }

  const submitForm = async (e) => {
    e.preventDefault();

    setFormLoading(true);

    let err = null;

    if (!hasBillingInfo && !isLoggedUserAdmin) {
      try {
        await api.createBillingInfo({
          ...formData,
        });
      } catch (e) {
        console.log("Error creating billing info");
        console.log(e);
        err = await handleFormResponseErrors(e);
      }
    }

    if (err) {
      alertError(t("alert.orders.errorOnNew"));
      unsetFormLoading(false);
      return;
    }

    try {
      const resp = await api.createDeliveryAccount(formData);
      setNewUserData(resp.resources[0]);
    } catch (e) {
      unsetFormLoading(false);
      err = await handleFormResponseErrors(e);
    }

    unsetFormLoading(false);

    if (err) {
      alertError(t("alert.orders.errorOnNew"));
      return;
    }

    nextStep();
    onFormSubmit();
  };

  // TODO: properly translate
  const BILLING_INFO_FIELDS = {
    firstname: "Nome",
    lastname: "Cognome",
    companyname: "Azienda",
    country: "Paese",
    state: "Provincia",
    address1: "Indirizzo",
    city: "Città",
    vat: "Partita IVA",
    recipient_code: "Codice destinatario",
    fiscalcode: "Codice fiscale",
    phonenumber: "Telefono",
  };

  return (
    <div className="screenContainer flexAlignCenter">
      {stepper}

      <div className="mt-4 w-full max-w-[--form-page-width]">
        <div className="sectionTitle">{t("delivery.accounts.add.stepRecap.title")}</div>
        <div className="sectionDesc">{t("delivery.accounts.add.stepRecap.desc")}</div>
      </div>
      <form className="formContainer" onSubmit={submitForm}>
        <div className="flex flex-col flex-grow w-full gap-10">
          <ServicesRecapTable
            className={"expand formSection"}
            tableName={formData.description}
            servicesData={[
              {
                name: t("delivery.accounts.add.stepRecap.serviceRecap.type"),
                value: getAccountTypeTranslation(formData.type),
              },
              {
                name: t("delivery.accounts.add.stepRecap.serviceRecap.plan"),
                value: t(`delivery.accounts.add.stepPlan.plan.${formData.plan}.label`),
              },
              {
                name: t("delivery.accounts.add.stepRecap.serviceRecap.volume"),
                value: formatter.format(selectedTier.quota),
              },

              formData.plan !== deliveryPlans.FREE && !hidePrices
                ? {
                    name: t("delivery.accounts.add.stepRecap.serviceRecap.paymentType"),
                    value: t("radiobtn.label.payment." + formData.payment_method),
                  }
                : null,
            ]}
          />

          {hidePrices ? (
            <div className="inputFull2">
              <WarningBanner
                color="yellow"
                msg="In fattura troverai il prezzo pattuito con il tuo fornitore"
                fullWidth
              />
            </div>
          ) : (
            <BillRecap
              className="expand"
              title={formData.plan === deliveryPlans.PREPAID ? t("label.billing.prepaid") : t("label.billing.monthly")}
              price={selectedTier.pricing_eur}
              appliedDiscount={appliedDiscount}
              infoFee={<TranslatedString>order.fee.priceVat</TranslatedString>}
              messages={[
                formData.plan === deliveryPlans.MONTHLY
                  ? t("delivery.accounts.add.stepRecap.billRecap.message.monthly")
                  : null,
              ]}
            />
          )}

          {formData.plan !== deliveryPlans.FREE && !hasBillingInfo && (
            <div className="mt-14">
              <div className="text-base settingTitle">Dati di fatturazione</div>
              <div className="">
                {Object.keys(BILLING_INFO_FIELDS).map((key) => (
                  <BillingInfoRow key={key} label={BILLING_INFO_FIELDS[key]} value={formData[key]} />
                ))}
              </div>
            </div>
          )}
        </div>

        {formErrors.onForm && (
          <div className="warningMsgFP">
            <WarningBanner color="red" msg={formErrors.onForm} fullWidth />
          </div>
        )}

        <div className="formFooter">
          <Button color="text" type="button" onClick={onFormCancel}>
            button.cancel
          </Button>
          <Button loading={isFormLoading} color="green">
            {isUpdating ? "button.order" : "button.createAccount"}
          </Button>
        </div>
      </form>

      {isUpdating && (
        <SmallModal modalClassName="" title={t("delivery.accounts.update.stepRecap.modal.title")} opened={isModalOpen}>
          <div className="section">
            {formData.plan === deliveryPlans.MONTHLY ? (
              <TranslatedString>delivery.accounts.update.stepRecap.modal.info.monthly</TranslatedString>
            ) : (
              t("delivery.accounts.update.stepRecap.modal.info.prepaid")
            )}
          </div>
          <div className="mb-4">
            <Button className="self-end" onClick={() => history.push("/delivery/accounts")}>
              button.ok
            </Button>
          </div>
        </SmallModal>
      )}
    </div>
  );
};

export default Recap;
